// =============================================================================
// SIREPPAR - Formulario de Partido (Alta/Edición)
// =============================================================================

import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import toast from 'react-hot-toast';
import { partidoService } from '../../services/api';

// =============================================================================
// Esquema de Validación
// =============================================================================

const partidoSchema = z.object({
  numero: z.number().min(1, 'Número requerido'),
  denominacion: z.string().min(3, 'Mínimo 3 caracteres').max(200),
  nota: z.string().max(2000).optional(),
  sigla: z.string().min(2, 'Mínimo 2 caracteres').max(20),
  // MUNICIPAL faltaba: los partidos municipales del reconocimiento no se podían
  // corregir desde la ficha interna (el select no lo ofrecía y el schema lo
  // rechazaba), aunque el enum TipoPartido de Prisma y el wizard externo sí lo usan.
  // Solo ámbitos territoriales: CONFEDERACION dejó de ser un `TipoPartido` y pasó a ser
  // un valor de `Partido.clase`, que no se edita desde este formulario.
  tipo: z.enum(['MUNICIPAL', 'PROVINCIAL', 'DISTRITO', 'NACIONAL']),
  fechaConstitucion: z.string().optional(),
  domicilioLegal: z.string().min(5, 'Domicilio requerido'),
  localidad: z.string().min(2, 'Localidad requerida'),
  codigoPostal: z.string().optional(),
  emailOficial: z.string().email('Email inválido').optional().or(z.literal('')),
  telefonoOficial: z.string().optional(),
  sitioWeb: z.string().url('URL inválida').optional().or(z.literal('')),
});

type PartidoFormData = z.infer<typeof partidoSchema>;

// =============================================================================
// Componente
// =============================================================================

export default function PartidoFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEdit = !!id;

  const [currentStep, setCurrentStep] = useState(0);
  const { data: partido, isLoading: loadingPartido } = useQuery({
    queryKey: ['partido', id],
    queryFn: async () => {
      const response = await partidoService.getById(id!);
      return response.data.data;
    },
    enabled: isEdit,
  });

  // Form
  const { register, handleSubmit, formState: { errors }, reset, watch } = useForm<PartidoFormData>({
    resolver: zodResolver(partidoSchema),
    defaultValues: {
      tipo: 'PROVINCIAL',
    },
  });

  // Cargar datos en el form cuando se edita
  useEffect(() => {
    if (partido && isEdit) {
      reset({
        numero: partido.numero,
        denominacion: partido.denominacion,
        nota: partido.nota || '',
        sigla: partido.sigla,
        tipo: partido.tipo,
        fechaConstitucion: partido.fechaConstitucion?.split('T')[0] || '',
        domicilioLegal: partido.domicilioLegal || '',
        localidad: partido.localidad || '',
        codigoPostal: partido.codigoPostal || '',
        emailOficial: partido.emailOficial || '',
        telefonoOficial: partido.telefonoOficial || '',
        sitioWeb: partido.sitioWeb || '',
      });
    }
  }, [partido, isEdit, reset]);

  // Mutation para crear/actualizar
  const mutation = useMutation({
    mutationFn: async (data: PartidoFormData) => {
      const payload = {
        ...data,
        fechaConstitucion: data.fechaConstitucion ? new Date(data.fechaConstitucion).toISOString() : undefined,
        emailOficial: data.emailOficial || undefined,
        sitioWeb: data.sitioWeb || undefined,
      };

      if (isEdit) {
        return partidoService.update(id!, payload);
      } else {
        return partidoService.create(payload);
      }
    },
    onSuccess: (response) => {
      toast.success(isEdit ? 'Partido actualizado correctamente' : 'Partido creado correctamente');
      queryClient.invalidateQueries({ queryKey: ['partidos'] });
      queryClient.invalidateQueries({ queryKey: ['partido', id] });
      navigate(`/partidos/${response.data.data.id}`);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || 'Error al guardar');
    },
  });

  const onSubmit = (data: PartidoFormData) => {
    mutation.mutate(data);
  };

  // Steps del formulario
  const steps = [
    { id: 'basico', title: 'Datos Básicos', description: 'Información principal del partido' },
    { id: 'contacto', title: 'Domicilio y Contacto', description: 'Información de contacto oficial' },
    { id: 'revision', title: 'Revisión', description: 'Confirmar datos ingresados' },
  ];

  const watchedData = watch();

  if (loadingPartido && isEdit) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <Link to="/partidos" className="text-gray-500 hover:text-gray-700 text-sm flex items-center gap-1 mb-2">
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Volver al listado
        </Link>
        <h1 className="text-2xl font-bold text-gray-900">
          {isEdit ? 'Editar Partido' : 'Nuevo Partido Político'}
        </h1>
        <p className="text-gray-500 mt-1">
          {isEdit ? `Editando: ${partido?.denominacion}` : 'Complete los datos para registrar un nuevo partido'}
        </p>
      </div>

      {/* Indicador de pasos */}
      <nav className="mb-8">
        <ol className="flex items-center">
          {steps.map((step, index) => (
            <li key={step.id} className={`relative ${index !== steps.length - 1 ? 'pr-8 sm:pr-20 flex-1' : ''}`}>
              {index !== steps.length - 1 && (
                <div className="absolute top-4 left-7 -right-3 sm:left-9 sm:-right-11">
                  <div className={`h-0.5 w-full ${index < currentStep ? 'bg-primary-600' : 'bg-gray-200'}`} />
                </div>
              )}
              <button
                type="button"
                onClick={() => setCurrentStep(index)}
                className="relative flex items-start group"
              >
                <span className="flex h-9 items-center">
                  <span className={`relative z-10 flex h-8 w-8 items-center justify-center rounded-full ${
                    index < currentStep
                      ? 'bg-primary-600 text-white'
                      : index === currentStep
                      ? 'border-2 border-primary-600 bg-white text-primary-600'
                      : 'border-2 border-gray-300 bg-white text-gray-500'
                  }`}>
                    {index < currentStep ? (
                      <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    ) : (
                      index + 1
                    )}
                  </span>
                </span>
                <span className="ml-4 min-w-0 flex flex-col">
                  <span className={`text-sm font-medium ${index === currentStep ? 'text-primary-600' : 'text-gray-500'}`}>
                    {step.title}
                  </span>
                  <span className="text-sm text-gray-500 hidden sm:block">{step.description}</span>
                </span>
              </button>
            </li>
          ))}
        </ol>
      </nav>

      {/* Formulario */}
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="bg-white rounded-lg shadow p-6">
          
          {/* Step 1: Datos Básicos */}
          {currentStep === 0 && (
            <div className="space-y-6">
              <h2 className="text-lg font-medium text-gray-900 border-b pb-2">Datos Básicos</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Número de Orden *
                  </label>
                  <input
                    type="number"
                    {...register('numero', { valueAsNumber: true })}
                    className={`w-full border rounded-lg px-3 py-2 ${errors.numero ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder="Ej: 1"
                  />
                  {errors.numero && <p className="text-red-500 text-sm mt-1">{errors.numero.message}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Sigla *
                  </label>
                  <input
                    type="text"
                    {...register('sigla')}
                    className={`w-full border rounded-lg px-3 py-2 ${errors.sigla ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder="Ej: PDP"
                  />
                  {errors.sigla && <p className="text-red-500 text-sm mt-1">{errors.sigla.message}</p>}
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Denominación *
                  </label>
                  <input
                    type="text"
                    {...register('denominacion')}
                    className={`w-full border rounded-lg px-3 py-2 ${errors.denominacion ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder="Nombre del partido"
                  />
                  {errors.denominacion && <p className="text-red-500 text-sm mt-1">{errors.denominacion.message}</p>}
                </div>

                {/* Mismo dato que edita la columna "Nota" de /partidos. */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nota interna
                  </label>
                  <textarea
                    rows={2}
                    maxLength={2000}
                    {...register('nota')}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    placeholder="Observaciones internas sobre el partido (opcional). Se ve en la columna Nota de Partidos."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tipo *
                  </label>
                  <select
                    {...register('tipo')}
                    className={`w-full border rounded-lg px-3 py-2 ${errors.tipo ? 'border-red-500' : 'border-gray-300'}`}
                  >
                    <option value="MUNICIPAL">Municipal</option>
                    <option value="PROVINCIAL">Provincial</option>
                    <option value="DISTRITO">Distrito</option>
                    <option value="NACIONAL">Nacional</option>
                  </select>
                  {errors.tipo && <p className="text-red-500 text-sm mt-1">{errors.tipo.message}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fecha de Constitución
                  </label>
                  <input
                    type="date"
                    {...register('fechaConstitucion')}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Contacto */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <h2 className="text-lg font-medium text-gray-900 border-b pb-2">Domicilio y Contacto</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Domicilio Legal *
                  </label>
                  <input
                    type="text"
                    {...register('domicilioLegal')}
                    className={`w-full border rounded-lg px-3 py-2 ${errors.domicilioLegal ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder="Dirección completa"
                  />
                  {errors.domicilioLegal && <p className="text-red-500 text-sm mt-1">{errors.domicilioLegal.message}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Localidad *
                  </label>
                  <input
                    type="text"
                    {...register('localidad')}
                    className={`w-full border rounded-lg px-3 py-2 ${errors.localidad ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder="Ciudad"
                  />
                  {errors.localidad && <p className="text-red-500 text-sm mt-1">{errors.localidad.message}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Código Postal
                  </label>
                  <input
                    type="text"
                    {...register('codigoPostal')}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    placeholder="9000"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email Oficial
                  </label>
                  <input
                    type="email"
                    {...register('emailOficial')}
                    className={`w-full border rounded-lg px-3 py-2 ${errors.emailOficial ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder="contacto@partido.org.ar"
                  />
                  {errors.emailOficial && <p className="text-red-500 text-sm mt-1">{errors.emailOficial.message}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Teléfono Oficial
                  </label>
                  <input
                    type="tel"
                    {...register('telefonoOficial')}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    placeholder="0280-4481234"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Sitio Web
                  </label>
                  <input
                    type="url"
                    {...register('sitioWeb')}
                    className={`w-full border rounded-lg px-3 py-2 ${errors.sitioWeb ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder="https://www.partido.org.ar"
                  />
                  {errors.sitioWeb && <p className="text-red-500 text-sm mt-1">{errors.sitioWeb.message}</p>}
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Revisión */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <h2 className="text-lg font-medium text-gray-900 border-b pb-2">Revisión de Datos</h2>
              
              <div className="bg-gray-50 rounded-lg p-4 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-sm text-gray-500">Número:</span>
                    <p className="font-medium">{watchedData.numero}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Sigla:</span>
                    <p className="font-medium">{watchedData.sigla}</p>
                  </div>
                  <div className="col-span-2">
                    <span className="text-sm text-gray-500">Denominación:</span>
                    <p className="font-medium">{watchedData.denominacion}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Tipo:</span>
                    <p className="font-medium">{watchedData.tipo}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Fecha Constitución:</span>
                    <p className="font-medium">{watchedData.fechaConstitucion || '-'}</p>
                  </div>
                </div>

                <hr />

                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <span className="text-sm text-gray-500">Domicilio:</span>
                    <p className="font-medium">{watchedData.domicilioLegal}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Localidad:</span>
                    <p className="font-medium">{watchedData.localidad}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">CP:</span>
                    <p className="font-medium">{watchedData.codigoPostal || '-'}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Email:</span>
                    <p className="font-medium">{watchedData.emailOficial || '-'}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Teléfono:</span>
                    <p className="font-medium">{watchedData.telefonoOficial || '-'}</p>
                  </div>
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm text-yellow-800">
                  <strong>Nota:</strong> Al guardar, el partido quedará en estado "En Formación". 
                  Deberá completar los requisitos legales para solicitar el reconocimiento.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Botones de navegación */}
        <div className="mt-6 flex justify-between">
          <button
            type="button"
            onClick={() => navigate('/partidos')}
            className="px-4 py-2 text-gray-700 hover:text-gray-900"
          >
            Cancelar
          </button>

          <div className="flex gap-3">
            {currentStep > 0 && (
              <button
                type="button"
                onClick={() => setCurrentStep(currentStep - 1)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Anterior
              </button>
            )}

            {currentStep < steps.length - 1 ? (
              <button
                type="button"
                onClick={() => setCurrentStep(currentStep + 1)}
                className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
              >
                Siguiente
              </button>
            ) : (
              <button
                type="submit"
                disabled={mutation.isPending}
                className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
              >
                {mutation.isPending ? 'Guardando...' : isEdit ? 'Actualizar Partido' : 'Crear Partido'}
              </button>
            )}
          </div>
        </div>
      </form>
    </div>
  );
}
